import { useState, useMemo, useEffect, ChangeEvent, useRef, Fragment } from 'react';
import tw from 'twin.macro';
import { CSSProp } from 'styled-components';

import { CaretIcon } from '@icons/CaretIcon';
import useOnClickOutside from '@app/hooks/useOnClickOutside';
import { getTextContent } from '@utils/commonUtils';
import { Loader } from '@components/Loader';

const Container = tw.div`relative inline-block`;
const SelectOptionContainer = tw.div`flex flex-col w-[130%] w-full h-auto max-h-[230px] overflow-auto bg-neutral-background border border-solid border-gray-2 rounded shadow-dropdown absolute top-9 left-0 overflow-x-hidden z-10 gap-0.5`;
const SearchInput = tw.input`block w-full text-xs placeholder:text-xs placeholder:italic leading-[14px] py-[9px] px-2.5 focus:border-0 focus:outline-none bg-neutral-background`;
const Divider = tw.div`w-auto border-b border-solid border-gray-2 h-[1px]`;
export const OptionWrapper = tw.div`hover:bg-gray-2 rounded-sm w-full text-text-and-icon`;
export const Option = tw.div`cursor-pointer truncate px-2 py-[6px] text-[11px] leading-4 font-medium text-text-and-icon`;
const Prefix = tw.span`-mr-1`;
const SelectLabel = tw.div`
  w-auto h-[28px] grid grid-cols-[1fr_14px] gap-1 items-center border border-solid border-gray-1 pr-2 rounded cursor-pointer shadow-dropdown
  [> div]:border-none
`;

const emptyOption: IDropdownOption = {
  value: null,
  label: <Option>None</Option>,
};

export interface IDropdownOption {
  value: string | null;
  label: string | JSX.Element;
  hasDivider?: boolean;

  // should be used for additional data if value is not enough
  meta?: { [key: string]: string | string[] | boolean | undefined };

  // should be used if we want to show different label on dropdown and on selected label
  selectedLabel?: string | JSX.Element;
}

interface IDropdownProps {
  value: IDropdownOption | null;
  options: IDropdownOption[];
  onChange: (val: IDropdownOption | IDropdownOption[]) => void;
  id?: string;
  initialSearchTerm?: string;
  isSearchable?: boolean;
  isLoading?: boolean;
  // todo: instead of showNoResults boolean we can add a custom component for no results
  showNoResults?: boolean;
  searchPlaceholder?: string;
  nullable?: boolean;
  nullableOption?: IDropdownOption;
  autoFocus?: boolean;
  labelStyle?: CSSProp;
  caretStyle?: CSSProp;
  dropdownMenuStyle?: CSSProp;
  isPresentational?: boolean;
  labelPrefix?: string;
  onClickOutside?: (selectedOption: IDropdownOption | null) => void;
  onOptionClick?: (option: IDropdownOption) => void;
  onSearch?: (searchTerm: string) => void;
}

export const Dropdown = ({
  id,
  value,
  options,
  isSearchable = false,
  isLoading = false,
  showNoResults = false,
  initialSearchTerm = '',
  searchPlaceholder = 'Search',
  onChange,
  nullable = false,
  nullableOption = emptyOption,
  autoFocus = false,
  labelStyle,
  caretStyle,
  dropdownMenuStyle,
  isPresentational = false,
  labelPrefix = '',
  onClickOutside,
  onOptionClick,
  onSearch,
  ...rest
}: IDropdownProps) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isFocused, setIsFocused] = useState<boolean>(autoFocus);
  const [searchTerm, setSearchTerm] = useState<string>(initialSearchTerm);
  useOnClickOutside(dropdownRef, () => {
    setIsFocused(false);
    onClickOutside && onClickOutside(selectedOption);
  });

  const _options = useMemo(() => {
    const arr = options;

    if (!searchTerm.length) {
      return arr;
    }

    if (onSearch) {
      onSearch(searchTerm);
      return arr;
    }

    return arr.filter((option) => {
      const labelText = getTextContent(option?.label);

      return labelText?.toLowerCase()?.startsWith(searchTerm.toLowerCase());
    });
  }, [searchTerm, options]);

  const _value = useMemo(() => (value ? value : nullable ? nullableOption : _options[0]), [value]);

  const [labelValue, setLabelValue] = useState(value);
  const [selectedOption, setSelectedOption] = useState<IDropdownOption | null>(_value);

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.currentTarget;
    setSearchTerm(value);
  };

  useEffect(() => {
    setLabelValue(value ? value : nullable ? nullableOption : _options[0]);
  }, [value, nullable]);

  useEffect(() => {
    if (selectedOption && onChange) {
      onChange(selectedOption);
      setSearchTerm('');
    }
  }, [selectedOption]);

  useEffect(() => {
    if (autoFocus) {
      setIsFocused(true);
    }
  }, [autoFocus]);

  return (
    <Container id={id} ref={dropdownRef}>
      <SelectLabel
        data-testid='dropdown-label'
        css={[
          isFocused && tw`border-accent-muted`,
          labelStyle,
          isPresentational && tw`cursor-auto [> div]:cursor-auto!`,
          labelPrefix.length && tw`grid-cols-[auto_1fr_14px]`,
        ]}
        onClick={() => !isPresentational && setIsFocused(!isFocused)}
        {...rest}>
        {labelPrefix && <Prefix>{labelPrefix}</Prefix>}
        {labelValue?.selectedLabel || labelValue?.label}
        {!isPresentational && <CaretIcon tw='fill-text-and-icon w-2.5 h-2.5' css={[caretStyle]} />}
      </SelectLabel>

      {isFocused && (
        <SelectOptionContainer data-testid='dropdown-options-container' css={[dropdownMenuStyle]}>
          {isSearchable && (
            <>
              <SearchInput
                autoFocus
                data-testid='dropdown-search'
                type='text'
                placeholder={searchPlaceholder}
                onChange={(e) => handleSearch(e as ChangeEvent<HTMLInputElement>)}
              />
              <Divider />
            </>
          )}

          {isLoading ? (
            <div tw='flex justify-center items-center h-[40px] w-full'>
              <Loader size='sm' theme='dark' />
            </div>
          ) : _options.length ? (
            _options.map((option: IDropdownOption, index) => (
              <Fragment key={option.value}>
                <OptionWrapper
                  onClick={() => {
                    setSelectedOption(option);
                    setIsFocused(false);
                    onOptionClick && onOptionClick(option);
                  }}
                  key={index}>
                  {option.label}
                </OptionWrapper>
                {option.hasDivider && <Divider data-testid='dropdown-divider' />}
              </Fragment>
            ))
          ) : (
            showNoResults && (
              <div tw='flex justify-center items-center h-[40px] w-full'>
                <p tw='text-text-muted-2 text-xs'>No results found</p>
              </div>
            )
          )}
        </SelectOptionContainer>
      )}
    </Container>
  );
};
